import { useCallback, useEffect, useRef, useState } from 'react';
import type { Game } from '../lib/api';

const ADVANCE_MS = 7000;

/**
 * Cycles the biggest games of the week instead of committing the whole banner to
 * one of them.
 *
 * A single featured game is a bet that the ranking got it right, and on a college
 * Saturday there are four or five games with a real claim to the slot. Showing one
 * at a time and moving on keeps the banner honest about that.
 *
 * It stops on hover, on focus, and while the tab is hidden, because a banner that
 * changes under a cursor on the way to a link is the most irritating thing a page
 * can do. Everything is reachable from the dots and arrows without waiting.
 */
export function FeatureCarousel({
  games,
  render,
}: {
  games: Game[];
  render: (g: Game) => React.ReactNode;
}) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = games.length;
  const wrap = useCallback((n: number) => ((n % count) + count) % count, [count]);

  // The index must stay inside the list when the schedule reloads with fewer games.
  useEffect(() => {
    if (i >= count && count) setI(0);
  }, [count, i]);

  const touch = useRef<number | null>(null);

  useEffect(() => {
    if (paused || count < 2) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') setI((n) => wrap(n + 1));
    }, ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, count, wrap]);

  if (!count) return null;
  const go = (step: number) => setI((n) => wrap(n + step));

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const from = touch.current;
        touch.current = null;
        if (from === null) return;
        const dx = e.changedTouches[0].clientX - from;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);   // swipe on a phone
      }}
      aria-roledescription="carousel"
      aria-label="Biggest games this week"
    >
      {render(games[Math.min(i, count - 1)])}

      {count > 1 && (
        <>
          {([['Previous game', -1, 'left-2 md:left-4', '‹'], ['Next game', 1, 'right-2 md:right-4', '›']] as const).map(
            ([label, step, side, glyph]) => (
              <button
                key={label}
                onClick={() => go(step)}
                aria-label={label}
                className={`absolute top-1/2 ${side} z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center
                            rounded-full bg-black/25 pb-0.5 font-cond text-[26px] leading-none text-paper
                            backdrop-blur-sm transition hover:bg-black/45 focus:outline-none
                            focus-visible:ring-2 focus-visible:ring-paper`}
              >
                {glyph}
              </button>
            ),
          )}

          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {games.map((g, n) => (
              <button
                key={g.id}
                onClick={() => setI(n)}
                aria-label={`Game ${n + 1} of ${count}`}
                aria-current={n === i}
                className={`h-1.5 rounded-full transition-all ${
                  n === i ? 'w-6 bg-paper' : 'w-1.5 bg-paper/50 hover:bg-paper/80'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
